import { format } from 'date-fns';
import {
  CubeIcon,
  CurrencyDollarIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { InventoryReport } from '../../services/reports';

interface InventoryReportViewProps {
  data: InventoryReport | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

export default function InventoryReportView({
  data,
  loading,
  error,
  onReload,
}: InventoryReportViewProps) {
  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-2 text-sm text-slate-600">Loading inventory report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={onReload}
              className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-indigo-200"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
        <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
          <dt>
            <div className="absolute rounded-md bg-gradient-to-br from-cyan-500 to-violet-500 p-3">
              <CubeIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-slate-500">Total Birds</p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <p className="text-2xl font-semibold text-slate-900">{data.birdInventory.totalBirds}</p>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
          <dt>
            <div className="absolute rounded-md bg-green-500 p-3">
              <CurrencyDollarIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-slate-500">Total Value</p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <p className="text-2xl font-semibold text-slate-900">
              R{Number(data.grandTotalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
          <dt>
            <div className="absolute rounded-md bg-slate-800 p-3">
              <CalendarIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-slate-500">Report Date</p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <p className="text-lg font-semibold text-slate-900">{format(new Date(data.reportDate || new Date()), 'MMM dd, yyyy')}</p>
          </dd>
        </div>
      </div>

      {/* Bird Inventory */}
      <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Bird Inventory by Species</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Species</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Count</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Avg Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status Breakdown</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.birdInventory?.bySpecies || []).map((species: any, index: number) => (
                  <tr key={index} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {species.species || 'Unknown Species'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{species.count || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      R{Number(species.averageValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      R{Number(species.totalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="flex space-x-2">
                        {(species.status?.breeding || 0) > 0 && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                            {species.status.breeding} Breeding
                          </span>
                        )}
                        {(species.status?.forSale || 0) > 0 && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-800">
                            {species.status.forSale} For Sale
                          </span>
                        )}
                        {(species.status?.personal || 0) > 0 && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-violet-100 text-violet-800">
                            {species.status.personal} Personal
                          </span>
                        )}
                        {(species.status?.other || 0) > 0 && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                            {species.status.other} Other
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!data.birdInventory?.bySpecies || data.birdInventory.bySpecies.length === 0) && (
            <div className="text-center py-8">
              <p className="text-slate-500">No bird inventory data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Feed Stock Inventory */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Feed Stock Inventory</h3>
            <p className="text-sm text-slate-600">
              Total Value: R{Number(data.feedInventory?.totalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Feed Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Current Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cost per Unit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.feedInventory?.items || []).map((item: any, index: number) => (
                  <tr key={index} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{item.name || 'Unknown'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{item.type || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{item.currentStock || 0} {item.unit || ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      R{Number(item.costPerUnit || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      R{Number(item.totalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.status === 'in-stock' ? 'bg-emerald-100 text-emerald-800' :
                        item.status === 'low-stock' ? 'bg-amber-100 text-amber-800' :
                        'bg-rose-100 text-rose-800'
                      }`}>
                        {(item.status || 'unknown').replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!data.feedInventory?.items || data.feedInventory.items.length === 0) && (
            <div className="text-center py-8">
              <p className="text-slate-500">No feed stock inventory data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Supplies Inventory */}
      <div className="bg-white overflow-hidden shadow rounded-lg mt-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Supplies Inventory</h3>
            <p className="text-sm text-slate-600">
              Total Value: R{Number(data.suppliesInventory?.totalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Supply Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Current Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cost per Unit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.suppliesInventory?.items || []).map((item: any, index: number) => (
                  <tr key={index} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{item.name || 'Unknown'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{item.category || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{item.currentStock || 0} {item.unit || ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      R{Number(item.costPerUnit || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      R{Number(item.totalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.status === 'in-stock' ? 'bg-emerald-100 text-emerald-800' :
                        item.status === 'low-stock' ? 'bg-amber-100 text-amber-800' :
                        'bg-rose-100 text-rose-800'
                      }`}>
                        {(item.status || 'unknown').replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!data.suppliesInventory?.items || data.suppliesInventory.items.length === 0) && (
            <div className="text-center py-8">
              <p className="text-slate-500">No supplies inventory data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
